import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import relatedProductService from '../../service/relatedproducts.service';
import { RelatedProduct } from '../../dto/relatedproducts.dto';
import { useCart } from '../../../contexts/CartContext';
import { useWishlist } from '../../../pages/wishlist/WishlistContext';
import { Heart } from 'lucide-react';
import LoadingSpinner from '../LodingSpiner/LodingSpiner';
import { CART_CONSTANTS } from '../FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import 'react-toastify/dist/ReactToastify.css';
import './styles/RelatedProducts.css';

interface RelatedProductsProps {
  productIdOrSlug: string;
}

const RelatedProducts: React.FC<RelatedProductsProps> = ({ productIdOrSlug }) => {
  const [products, setProducts] = useState<RelatedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, addToCart } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isItemInWishlist } = useWishlist();
  const lastWishlistActionRef = useRef<number>(0);

  useEffect(() => {
    if (productIdOrSlug) {
      fetchRelatedProducts();
    }
  }, [productIdOrSlug]);

  const fetchRelatedProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await relatedProductService.getRelatedProducts(productIdOrSlug);
      if (response.success && response.data) {
        setProducts(response.data as RelatedProduct[]);
      } else {
        setError('Failed to load related products');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching related products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product: RelatedProduct) => {
    try {
      let imgSrc = product.imageUrl;
      if (!imgSrc && Array.isArray(product.images) && product.images.length > 0 && product.images[0].url) {
        imgSrc = product.images[0].url;
      }
      
      const cartProduct = {
        id: product._id,
        _id: product._id,
        title: product.title || 'Unnamed Product',
        name: product.title || 'Unnamed Product',
        price: product.price || 0,
        imageUrl: imgSrc || '',
        description: product.description || '',
        quantity: 1
      } as const;
      
      addToCart(cartProduct, 1);
    } catch (error) {
    }
  };

  const handleCartQuantityChange = (productId: string, newQuantity: number) => {
    const cartItem = cartItems.find(item => {
      if (item.productId !== productId) return false;
      return !item.variantSku || item.variantSku === '';
    });

    if (!cartItem) return;

    if (newQuantity <= 0) {
      removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
    } else {
      updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
    }
  };

  const handleWishlistToggle = (product: RelatedProduct) => {
    const now = Date.now();
    if (now - lastWishlistActionRef.current < 300) return;
    lastWishlistActionRef.current = now;

    try {
      const isWishlisted = isItemInWishlist(product._id);

      if (isWishlisted) {
        removeFromWishlist(product._id);
      } else {
        const imageUrl = (() => {
          if (Array.isArray(product.images) && product.images.length > 0 && product.images[0].url) {
            return product.images[0].url;
          }
          if (product.imageUrl) return product.imageUrl;
          if (Array.isArray(product.variants)) {
            const variantWithImage = product.variants.find(v => v.variantImage && v.variantImage.trim());
            if (variantWithImage) return variantWithImage.variantImage;
          }
          return 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
        })();

        const productForWishlist = {
          id: product._id,
          _id: product._id,
          title: product.title || 'Unnamed Product',
          slug: product.slug,
          description: product.description || '',
          price: product.price || 0,
          compareAtPrice: (product as any).compareAtPrice ?? null,
          quantity: (product as any).quantity ?? 0,
          images: imageUrl ? [imageUrl] : [],
          isActive: (product as any).isActive,
          isFeatured: (product as any).isFeatured ?? false,
          hasVariants: Array.isArray(product.variants) && product.variants.length > 0,
          categories: ((product as any).categories || []).map((c: any) => c.name),
          tags: (product as any).tags || []
        } as any;

        addToWishlist(productForWishlist);
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  };

  if (loading) {
    return (
      <div className="related-products-section" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="related-products-section">
        <div className="error-message">
          {error}
          <button onClick={fetchRelatedProducts} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <div className="related-products-section">
      <div className="related-products-container">
        <h2 className="related-products-title">Related Products</h2>
        <div className="rp-products-row">
          {products.map((product) => {
            let imageUrl = '';
            let variantSku = '';
            if (Array.isArray(product.images) && product.images.length > 0 && product.images[0].url && product.images[0].url.trim()) {
              imageUrl = product.images[0].url;
            } else if (product.imageUrl && product.imageUrl.trim()) {
              imageUrl = product.imageUrl;
            } else if (Array.isArray(product.variants)) {
              const variantWithImage = product.variants.find(v => v.variantImage && v.variantImage.trim());
              if (variantWithImage) {
                imageUrl = variantWithImage.variantImage;
                variantSku = variantWithImage.sku || '';
              } else {
                imageUrl = 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
              }
            } else {
              imageUrl = 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
            }
            const isWishlisted = isItemInWishlist(product._id);
            const cartItem = cartItems.find(item => {
              if (item.productId !== product._id) return false;
              return !item.variantSku || item.variantSku === '';
            });
            const isInCart = !!cartItem;
            const cartQuantity = cartItem?.quantity || 0;
            return (
              <div 
                key={product._id} 
                className="related-product-card"
                style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => navigate(`/product/${product.slug || product._id}${variantSku ? `?variant=${encodeURIComponent(variantSku)}` : ''}`)}
              >
                <div 
                  className="wishlist-icon" 
                  title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'} 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleWishlistToggle(product);
                  }}
                  role="button" 
                  aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                  style={{ cursor: 'pointer', position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 10 }}
                >
                  <Heart 
                    size={22} 
                    color={isWishlisted ? '#e0245e' : '#000000ff'} 
                    strokeWidth={2.2} 
                    fill={isWishlisted ? '#e0245e' : 'none'} 
                  />
                </div>

                <div className="product-image-container">
                  <img 
                    src={imageUrl}
                    alt={product.title}
                    className="product-image"
                  />
                </div>
                <div className="product-info">
                  <h3 className="product-title">{product.title}</h3>
                  <div className="product-price-actions">
                    <p className="product-price">₹{Math.round(product.price || 0)}</p>
                    {isInCart ? (
                      <div className="cart-quantity-controls">
                        <button
                          type="button"
                          className="cart-quantity-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCartQuantityChange(product._id, cartQuantity - 1);
                          }}
                          style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                          aria-label="Decrease quantity"
                        >
                          -
                        </button>
                        <span className="cart-quantity-display">{cartQuantity}</span>
                        <button
                          type="button"
                          className="cart-quantity-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
                              handleCartQuantityChange(product._id, cartQuantity + 1);
                            }
                          }}
                          style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                          aria-label="Increase quantity"
                          disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button 
                        type="button"
                        className="add-to-cart-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddToCart(product);
                        }}
                        onTouchEnd={(e) => { e.stopPropagation(); }}
                      >
                      Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RelatedProducts;